import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slider,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { contentFactoryMap } from "../../../utils/constants";
import CloseIcon from "@mui/icons-material/Close";
import useLanguage from "../../../hooks/useLanguage";
import MarkdownEditor from "../../../components/MarkdownEditor";
import UploadArea from "../../../components/uploadArea";
import { FormRow, HelperCaption, FieldGroup } from "../styles";
import { useState, useEffect, useMemo } from "react";
import useContent from "../../../hooks/useContent";
import formTypes from "./form.types";
import useToaster from "../../../hooks/useToaster";
import OverlayPicker from "../../../components/overlayPicker";

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== "$direction",
})`
  direction: ${({ $direction }) => $direction};
  &.rtl,
  &.rtl * {
    text-align: right;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  justify-content: space-between;
  display: flex;
  align-items: center;
`;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
`;

const StyledDialogActions = styled(DialogActions, {
  shouldForwardProp: (prop) => prop !== "$rtl",
})`
  ${({ $rtl }) =>
    $rtl &&
    `
    flex-direction: row-reverse;
    justify-content: flex-start;
  `}
`;

function PopUp({
  open,
  type,
  title,
  content,
  disabled,
  loading,
  maxWidth,
  onSave,
  onClose,
}) {
  const [
    {
      direction,
      pages: {
        content: { popup: t },
      },
    },
  ] = useLanguage();

  return (
    <StyledDialog
      maxWidth={maxWidth || "sm"}
      $direction={direction}
      className={direction}
      open={open}
      fullWidth
    >
      <StyledDialogTitle>
        {title || t[type]?.title}
        <IconButton onClick={onClose}>
          <CloseIcon fontSize="medium" />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>{content}</StyledDialogContent>
      <StyledDialogActions $rtl={direction === "rtl"}>
        <Button
          variant="outlined"
          color="actions"
          disabled={disabled}
          onClick={onClose}
        >
          {t.cancel}
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={disabled}
          onClick={onSave}
          endIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t.save}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
}

export default function NewContentPopup({ ...args }) {
  const [
    {
      pages: {
        content: { popup: t },
      },
    },
  ] = useLanguage();
  const { addToaster } = useToaster();
  const { generateVideoContent, generateContent, createContent, reframeVideo } =
    useContent();
  const [form, setForm] = useState(formTypes[args.type] || {});
  const [sending, setSending] = useState(false);
  const [overlay, setOverlay] = useState({ enabled: false, payload: null, valid: true });
  const text = useMemo(() => t[args.type] || {}, [t, args.type]);

  const handleChange = (e) => {
    const { id, value, name } = e.target;
    setForm((prev) => ({ ...prev, [id || name]: value }));
  };

  useEffect(() => {
    return () => {
      if (form.url && form.url.startsWith("blob:")) {
        URL.revokeObjectURL(form.url);
      }
    };
  }, [form.url]);

  useEffect(() => {
    setForm(formTypes[args.type] || {});
    setOverlay({ enabled: false, payload: null, valid: true });
  }, [args.type]);

  switch (args?.type) {
    case contentFactoryMap.article:
      const handleArticleSave = async () => {
        setSending(true);
        await createContent({
          title: form.title,
          content: form.content,
          source: "manual",
          type: "text",
        });
        addToaster({
          message: text.success,
          severity: "success",
        });
        setSending(false);
        args.onClose();
      };

      return (
        <PopUp
          maxWidth="md"
          {...args}
          onSave={handleArticleSave}
          type={contentFactoryMap.article}
          disabled={sending}
          loading={sending}
          content={
            <>
              <TextField
                required
                id="title"
                label={text.articleTitle}
                fullWidth
                value={form.title}
                onChange={handleChange}
              />
              <MarkdownEditor
                value={form.content}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, content: value }))
                }
              />
            </>
          }
        />
      );
    case contentFactoryMap.contentCreator:
      const handleContentCreatorSave = async () => {
        setSending(true);
        await generateContent(form);
        addToaster({
          message: text.success,
          severity: "success",
        });
        setSending(false);
        args.onClose();
      };

      return (
        <PopUp
          maxWidth="sm"
          {...args}
          type={contentFactoryMap.contentCreator}
          disabled={sending}
          loading={sending}
          onSave={handleContentCreatorSave}
          content={
            <>
              <TextField
                required
                id="prompt"
                label={text.prompt}
                helperText={text.promptHelper}
                multiline
                rows={3}
                fullWidth
                value={form.prompt}
                onChange={handleChange}
              />
              <TextField
                select
                id="lang"
                name="lang"
                label={text.language}
                helperText={text.languageHelper}
                value={form.lang}
                onChange={handleChange}
              >
                {Object.keys(text.languages).map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {text.languages[lang]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                id="type"
                name="type"
                label={text.contentType}
                helperText={text.contentTypeHelper}
                value={form.type}
                onChange={handleChange}
              >
                {Object.keys(text.contentTypes).map((type) => (
                  <MenuItem key={type} value={type}>
                    {text.contentTypes[type]}
                  </MenuItem>
                ))}
              </TextField>
              <FormRow>
                <TextField
                  id="minLength"
                  name="minLength"
                  label={text.minLength}
                  type="number"
                  value={form.minLength}
                  onChange={handleChange}
                />
                <TextField
                  id="maxLength"
                  name="maxLength"
                  label={text.maxLength}
                  type="number"
                  value={form.maxLength}
                  onChange={handleChange}
                />
                <TextField
                  select
                  id="maxSources"
                  name="maxSources"
                  label={text.maxSources}
                  helperText={text.maxSourcesHelper}
                  value={form.maxSources}
                  onChange={handleChange}
                >
                  {[20, 50, 100].map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </TextField>
              </FormRow>

              <FormControlLabel
                control={
                  <Checkbox
                    id="noImages"
                    name="noImages"
                    checked={form.noImages}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        noImages: e.target.checked,
                      }))
                    }
                  />
                }
                label={text.noImages}
              />
            </>
          }
        />
      );
    case contentFactoryMap.videoContentCreator:
      const handleVideoContentSave = async () => {
        setSending(true);
        await generateVideoContent({
          files: form.files,
          paragraphs: form.paragraphsTouched ? form.paragraphs : undefined,
        });
        addToaster({ message: text.success, severity: "success" });
        setSending(false);
        args.onClose();
      };

      return (
        <PopUp
          maxWidth="sm"
          onSave={handleVideoContentSave}
          disabled={sending || !form.files?.length}
          loading={sending}
          {...args}
          type={contentFactoryMap.videoContentCreator}
          content={
            <>
              <Alert severity="info" variant="outlined">
                {text.alert}
              </Alert>
              <UploadArea
                multiple
                files={form.files}
                onFilesChange={(newFiles) =>
                  setForm((prev) => ({ ...prev, files: newFiles }))
                }
                accept="video/*"
                loading={sending}
              />
              <HelperCaption variant="caption" color="text.secondary">
                {text.fileHelper}
              </HelperCaption>
              <Typography variant="body2" color="text.secondary">
                {text.paragraphsLabel}
              </Typography>
              <Slider
                id="paragraphs"
                name="paragraphs"
                valueLabelDisplay="auto"
                step={1}
                marks
                min={3}
                max={10}
                value={form.paragraphs ?? 6}
                onChange={(e, val) =>
                  setForm((prev) => ({
                    ...prev,
                    paragraphs: val,
                    paragraphsTouched: true,
                  }))
                }
              />
              <HelperCaption variant="caption" color="text.secondary">
                {text.paragraphsHelper}
              </HelperCaption>
            </>
          }
        />
      );
    case contentFactoryMap.reframer:
      const handleReframerSetFile = (file) =>
        setForm((prev) => ({
          ...prev,
          file,
          url: file ? URL.createObjectURL(file) : null,
        }));

      const handleReframerSave = async () => {
        if (overlay.enabled && !overlay.valid) {
          addToaster({ message: "Fill in all overlay fields before submitting", severity: "error" });
          return;
        }
        setSending(true);
        await reframeVideo({
          name: form.name,
          file: form.file,
          overlay: overlay.enabled ? overlay.payload : undefined,
        });
        addToaster({ message: text.success, severity: "success" });
        setSending(false);
        args.onClose();
      };

      return (
        <PopUp
          maxWidth="sm"
          {...args}
          type={contentFactoryMap.reframer}
          disabled={sending}
          loading={sending}
          onSave={handleReframerSave}
          content={
            <>
              <TextField
                required
                id="name"
                label={text.name}
                fullWidth
                value={form.name}
                onChange={handleChange}
              />
              <UploadArea file={form.file} setFile={handleReframerSetFile} loading={sending} />
              {form.url && (
                <video
                  src={form.url}
                  controls
                  style={{ width: "100%", borderRadius: 8 }}
                />
              )}
              <OverlayPicker value={overlay} onChange={setOverlay} />
            </>
          }
        />
      );
  }
}
